/**
 * OwnerClientNotesSection — Sawyer 11 client-notes-agreements (F1).
 *
 * Client context reaches whoever is handling the dog: the owner's pinned
 * notes (and any note new to me, tagged "New") on the dog's profile, right
 * under the Admission decision (refuter C7: alerts → admission → client
 * notes → packages), with the way to the client record.
 *
 * Renders NOTHING when the owner has no pinned and no new notes, for roles
 * without `customers:read:all` (no request is sent), and when the read
 * fails or the server predates this lane — the dog page stands on its own
 * (OwnerPackagesSection's posture), and nothing logs. When the section shows
 * every note new to me it marks them seen, like the desk digest.
 */
import { useEffect, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { ClientNotesDigest } from '../customers/ClientNotesDigest.js';
import { useClientNoteSignal } from '../customers/client-note-signals.js';
import { ProfileSection } from './ProfileSection.js';

export interface OwnerClientNotesSectionProps {
  readonly apiUrl: string;
  readonly owner: {
    readonly customerId: string;
    readonly firstName: string;
    readonly lastName: string;
  };
}

export function OwnerClientNotesSection({ apiUrl, owner }: OwnerClientNotesSectionProps): JSX.Element | null {
  const { signal, markSeen } = useClientNoteSignal(apiUrl, owner.customerId);
  const hasAnything = signal !== null && (signal.pinned_total > 0 || signal.new_notes.length > 0);

  useEffect(() => {
    if (hasAnything) markSeen();
  }, [hasAnything, markSeen]);

  if (!signal || !hasAnything) return null;
  const ownerName = `${owner.firstName} ${owner.lastName}`.trim() || 'The owner';
  return (
    <ProfileSection
      title="Client notes"
      testId="dogprofile-client-notes"
      action={
        <Link
          className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
          to={`/customers/${owner.customerId}`}
          data-testid="dogprofile-client-notes-owner-link"
        >
          Open client →
        </Link>
      }
    >
      <p className="ferro-dogprofile__note-text" data-testid="dogprofile-client-notes-owner">
        Notes on {ownerName}&rsquo;s account.
      </p>
      <ClientNotesDigest
        pinned={signal.pinned}
        newNotes={signal.new_notes}
        pinnedTotal={signal.pinned_total}
        unseenCount={signal.unseen_count}
        customerId={owner.customerId}
        mode="pinned-and-new"
        hideOpenClient={signal.unseen_count <= signal.new_notes.length}
        testId="dogprofile-client-notes-digest"
      />
    </ProfileSection>
  );
}
